import {
  User,
  VoucherApproval,
  VoucherApprovalStageStatus,
  VoucherType,
  Roles,
  Branch,
  VoucherState,
} from '../types'

export function getCanChangeDepartment(
  voucher: VoucherType,
  roleId?: number | null,
  email?: string | null,
  userId?: string | null,
): boolean {
  const allowedOnBehalfOf = isAllowedOnBehalfOf(roleId, email)
  const isNationalSecMall = roleId === Roles.NATIONAL.MAAL.id
  return (
    (allowedOnBehalfOf && voucher.state === VoucherState.DRAFT) ||
    (isNationalSecMall &&
      voucher.state === VoucherState.SUBMITTED &&
      (voucher.workflow?.id === 2 || // Nat. Sec./Nat. Amin/Nat. Missionary/Local Sadr/Others
        voucher.workflow?.id === 5 || // Amir
        voucher.workflow?.id === 6)) || // National Secretary Maal
    // Local Missionary: National Maal is the second approver, so IN_PROGRESS
    (isNationalSecMall &&
      voucher.state === VoucherState.IN_PROGRESS &&
      voucher.workflow?.id === 4 &&
      getCurrentApprover(voucher)?.id === userId)
  )
}

export function getFileExtension(filename: string): string | null {
  const parts = filename.split('.')
  return parts.length > 1 ? (parts.pop() ?? null) : null
}

export function isGenSec(email?: string | null): boolean {
  return !!email && /^gen\.sec[^@]*@ahmadiyya\.se$/i.test(email)
}

export function isAllowedOnBehalfOf(
  roleId?: number | null,
  email?: string | null,
): boolean {
  return (
    roleId === Roles.NATIONAL.MAAL.id ||
    roleId === Roles.NATIONAL.ADMIN.id ||
    roleId === Roles.BRANCH.SADR.id ||
    roleId === Roles.BRANCH.MAAL.id ||
    roleId === Roles.BRANCH.AMIN.id ||
    roleId === Roles.BRANCH.MISSIONARY.id ||
    isGenSec(email)
  )
}

export function getLatestApprovingUser(voucher: VoucherType): User | null {
  if (!voucher.voucher_approvals || voucher.voucher_approvals.length === 0) {
    return null
  }

  const latestApproval = voucher.voucher_approvals
    .filter((a) => a.approved_at !== null && a.status === 'APPROVED')
    .sort((a, b) => b.order_number - a.order_number)[0]

  return latestApproval?.approver ?? null
}

export function getCurrentApprover(voucher: VoucherType): User | null {
  return (
    voucher.voucher_approvals
      ?.filter((va) => va.status === VoucherApprovalStageStatus.PENDING)
      .sort((a, b) => a.order_number - b.order_number)[0]?.approver ?? null
  )
}

// Mirrors the backend's getNextVoucherState (vhs-gql/src/lib/approvers.ts): a voucher
// reaches APPROVED once every non-payment approval stage is APPROVED. Used to decide,
// before submitting this approval, whether it's the one that will draw on the budget.
export function willVoucherReachApproved(voucher: VoucherType): boolean {
  const nonPaymentApprovals = voucher.voucher_approvals.filter(
    (va) => !va.approval_stage?.is_payment_stage,
  )
  const stillPending = nonPaymentApprovals.filter(
    (va) => va.status === VoucherApprovalStageStatus.PENDING,
  )
  return stillPending.length <= 1
}

export function getMyApproval(
  voucher: VoucherType,
  userId: string,
): VoucherApproval | null {
  const approval = voucher.voucher_approvals.find(
    (va) => va.approver.id === userId,
  )
  return approval || null
}

export function getMyLatestActionTime(
  voucher: VoucherType,
  userId: string,
): number {
  const times = voucher.voucher_approvals
    .filter(
      (va) =>
        va.approver.id === userId &&
        (va.status === 'APPROVED' ||
          va.status === 'REJECTED' ||
          va.status === 'PAID'),
    )
    .map((va) => toDate(va.approved_at)?.getTime() ?? 0)

  return times.length > 0 ? Math.max(...times) : 0
}

export function isRoleAmin(role: string): boolean {
  return role === 'AMIN' || role === 'LOCAL_AMIN'
}

export function isRoleAllowedPayments(roleId: number): boolean {
  return roleId === 2 || roleId === 3 || roleId === 5
}

export function convertToIsoString(date: number): string {
  return new Date(date * 1).toISOString().split('T')[0]
}

export const toDate = (val?: string | number | null): Date | null => {
  if (val === null || val === undefined || val === '') return null
  const d = new Date(
    typeof val === 'string' && /^\d+$/.test(val) ? Number(val) : val,
  )
  return isNaN(d.getTime()) ? null : d
}

export const formatPaymentDate = (dateVal?: string | number | null) => {
  const d = toDate(dateVal)
  return d ? d.toISOString().split('T')[0] : ''
}

export const getWaitingSinceValue = (
  voucher: Pick<VoucherType, 'submitted_at' | 'voucher_approvals'>,
): string | number | null => {
  const sortedApprovals = [...voucher.voucher_approvals].sort(
    (a, b) => a.order_number - b.order_number,
  )
  const currentPending = sortedApprovals.find((va) => va.status === 'PENDING')
  if (!currentPending) return voucher.submitted_at

  let latest: { raw: string; time: number } | null = null
  for (const va of sortedApprovals) {
    if (va.order_number >= currentPending.order_number) break
    const d = toDate(va.approved_at)
    if (d && (!latest || d.getTime() > latest.time)) {
      latest = { raw: va.approved_at as string, time: d.getTime() }
    }
  }

  return latest ? latest.raw : voucher.submitted_at
}

export const formatWaitingSince = (submittedAt?: string | number | null) => {
  const submitted = toDate(submittedAt)
  if (!submitted) return ''

  const days = Math.floor(
    (Date.now() - submitted.getTime()) / (1000 * 60 * 60 * 24),
  )

  if (days <= 0) return 'Today'
  if (days === 1) return '1 day'
  return `${days} days`
}

export const REPORTS_FULL_ACCESS_DEPARTMENTS: ReadonlyArray<string> = [
  'Amin',
  'Maal',
  'Muhasib',
  'Internal Auditor',
  'Amir',
  'General Secretary',
  "Sadr Jama'at",
]

const BRANCH_REPORTS_DEPARTMENTS: ReadonlyArray<string> = [
  'Maal',
  'Amin',
  'General Secretary',
]

export function hasReportsAccess(
  branch?: string | null,
  departmentName?: string | null,
  userRoleCode?: string | null,
): boolean {
  if (branch === 'NATIONAL') return true
  if (BRANCH_REPORTS_DEPARTMENTS.includes(departmentName ?? '')) return true
  if (departmentName === "Sadr Jama'at" && userRoleCode === 'SADR_BRANCH')
    return true
  return false
}

export function hasFullReportsAccess(departmentName?: string | null): boolean {
  return REPORTS_FULL_ACCESS_DEPARTMENTS.includes(departmentName ?? '')
}

export const getCurrentExpensePeriod = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  return parseInt(`${year}${month < 10 ? '0' : ''}${month}`)
}

export const getExpenseMonthOptions = (basePeriod: number) => {
  const year = Math.floor(basePeriod / 100)
  const month = (basePeriod % 100) - 1 // Date months are 0-indexed
  const baseDate = new Date(year, month, 1)

  const options: Array<{ key: number; value: string }> = []

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const getMonthData = (d: Date) => {
    const year = d.getFullYear()
    const month = d.getMonth()
    const key = parseInt(`${year}${month + 1 < 10 ? '0' : ''}${month + 1}`)
    const value = `${monthNames[month]} ${year}`
    return { key, value }
  }

  // Range from -3 to +3
  for (let i = -3; i <= 3; i++) {
    const d = new Date(baseDate)
    d.setMonth(baseDate.getMonth() + i)
    options.push(getMonthData(d))
  }

  return options
}

export const formatPeriod = (period: number | null | undefined) => {
  if (!period) return ''
  const year = Math.floor(period / 100)
  const month = period % 100

  const monthName = new Date(0, month - 1).toLocaleString('en-US', {
    month: 'short',
  })

  return `${monthName} ${year}`
}

export const getDefaultFinancialYear = () => {
  const now = new Date()
  return now.getMonth() + 1 >= 7 ? now.getFullYear() + 1 : now.getFullYear()
}

export function findCurrentFinancialYear(financialYears: any[]) {
  const today = new Date()
  return (
    financialYears.find((fy: any) => {
      const start = new Date(Number(fy.start_date))
      const end = new Date(Number(fy.end_date))
      return today >= start && today <= end
    })?.id || undefined
  )
}

// Every month in the financial year, capped at the current month so future
// (not-yet-started) periods aren't offered as filter options.
export function getFinancialYearPeriods(financialYear: {
  start_date: string | number
  end_date: string | number
}): number[] {
  const start = new Date(Number(financialYear.start_date))
  const end = new Date(Number(financialYear.end_date))
  const now = new Date()

  const periods: number[] = []
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1)
  while (cursor <= end && cursor <= now) {
    const year = cursor.getFullYear()
    const month = cursor.getMonth() + 1
    periods.push(parseInt(`${year}${month < 10 ? '0' : ''}${month}`))
    cursor.setMonth(cursor.getMonth() + 1)
  }
  return periods
}

export function getAvailableDepartments(
  departments: any[],
  authUser: any,
  voucherBranch?: string | null,
): any[] {
  return departments.filter((d: any) => {
    const isNational = (voucherBranch || authUser.branch) === Branch.NATIONAL
    const baseFilter = isNational ? true : !d.national_only

    if (authUser.role_id === Roles.BRANCH.MISSIONARY.id) {
      const isOwnDept = d.id === authUser.department_id
      const isTarbiyyatOrTabligh =
        d.name.toLowerCase() === 'tarbiyyat' ||
        d.name.toLowerCase() === 'tabligh'
      return baseFilter && (isOwnDept || isTarbiyyatOrTabligh)
    }

    return baseFilter
  })
}

export function getDepartmentUser(
  users: User[],
  selectedDepartmentId: string,
  loggedInUserBranch: string,
) {
  const representative = users.find(
    (u: User) =>
      u.department?.id === selectedDepartmentId &&
      u.branch === loggedInUserBranch,
  )
  const payTo = representative
    ? `${representative.first_name} ${representative.last_name}`
    : undefined
  return payTo
}
